import { Group, Stack } from "@mantine/core"
import { PaginationState, RowSelectionState } from "@tanstack/react-table"
import { ColumnManager, FilterBuilder } from "@tfg/ui"
import { Dispatch, SetStateAction, useState } from "react"
import { useTranslation } from "react-i18next"

import { ProductResponses } from "@tfg/types/endpoints/products"
import { TABLE_CONTEXT_MAP } from "@tfg/types/schemas/modules/view/constants/table-context-map"
import { fetchDataOptionsValidationType } from "@tfg/types/schemas/products"

import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import { CustomTable } from "~/components/table/CustomTable"
import { SearchBar } from "~/components/table/SearchBar"
import { ViewHeader } from "~/components/table/views/ViewHeader"
import { Views } from "~/components/table/views/Views"
import { FindProductsCatalogRequestFiltersDto } from "~/gen/api"
import { useGetColumnManagerTranslations } from "~/hooks/table"
import { useBuildProductFilters } from "~/pages/Brand/Products/List/hooks/useBuildProductFilters"
import { AgecBulkActions } from "~/pages/Brand/Regulations/AGEC/AgecBulkActions.tsx"
import { useAgecColumns } from "~/pages/Brand/Regulations/AGEC/columns.tsx"

interface Props {
  data: ProductResponses.GetProducts
  fetchDataOptions: fetchDataOptionsValidationType
  setPagination: Dispatch<SetStateAction<PaginationState>>
  filtersValue: FindProductsCatalogRequestFiltersDto
  setFiltersValue: Dispatch<
    SetStateAction<FindProductsCatalogRequestFiltersDto>
  >
  isFetching: boolean
}

export const ProductsAGECTable = ({
  data,
  fetchDataOptions,
  setPagination,
  filtersValue,
  setFiltersValue,
  isFetching,
}: Props) => {
  const columns = useAgecColumns()
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState<boolean>(false)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const { t } = useTranslation("regulations")
  const { t: tProducts } = useTranslation("products")
  const columnManagerTranslations = useGetColumnManagerTranslations()
  const { title, searchPlaceholder, options } =
    useBuildProductFilters<FindProductsCatalogRequestFiltersDto>([
      {
        label: t("lists.placeholders.agec"),
        key: "agecCompliance",
        type: "select",
        options: [
          { value: "yes", label: t("lists.placeholders.yes") },
          { value: "no", label: t("lists.placeholders.no") },
        ],
      },
    ])

  const table = useCustomReactTable({
    state: {
      pagination: {
        pageIndex: fetchDataOptions.skip / fetchDataOptions.take,
        pageSize: fetchDataOptions.take,
      },
      rowSelection,
    },

    initialColumnPinning: { left: ["name"] },
    manualPagination: true,
    data: data.products,
    getRowId: (row) => row.id.toString(),
    columns,
    rowCount: data.totalProducts,
    onPaginationChange: setPagination,

    onRowSelectionChange: setRowSelection,
  })

  return (
    <Stack>
      <Views tableContext={TABLE_CONTEXT_MAP.AGEC} />
      <Group>
        <SearchBar
          filtersValue={filtersValue}
          setFiltersValue={setFiltersValue}
          placeholder={tProducts("lists.placeholders.searchProduct")}
          isFetching={isFetching}
        />
        <FilterBuilder<FindProductsCatalogRequestFiltersDto>
          title={title}
          searchPlaceholder={searchPlaceholder}
          options={options}
          filters={filtersValue}
          setFilters={setFiltersValue}
        />
        <ColumnManager
          table={table}
          translations={columnManagerTranslations}
          isOpen={isColumnManagerOpen}
          setIsOpen={setIsColumnManagerOpen}
        />
        <AgecBulkActions table={table} />
        <ViewHeader tableContext={TABLE_CONTEXT_MAP.AGEC} />
      </Group>
      <CustomTable
        table={table}
        pagination={true}
        manualPaginationCount={data.totalProducts}
        layout="fixed"
        setOpenColumnManager={setIsColumnManagerOpen}
      />
    </Stack>
  )
}
